/**
 * Marketing attribution captured from the signup URL.
 *
 * The landing pages (separate repo/domain) append GA4 + UTM query params to
 * every signup link. We persist them to localStorage so they survive the whole
 * signup flow (multi-step forms, OAuth redirects can drop the querystring) and
 * a later return visit (new tab, bookmark, typed URL), then forward them to the
 * account-creation API. A URL with new params replaces the saved set (the most
 * recent campaign wins); saved params expire after ATTRIBUTION_TTL_MS and are
 * cleared once an account is created.
 *
 * Stored keys are camelCase to match the shape persisted on the user document.
 */
import { trackingOptedOut } from "src/utils/consent";

export interface Attribution {
  gaClientId?: string;
  gaSessionId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmPlacement?: string;
  gclid?: string;
  fbclid?: string;
  /** Landing-page A/B experiment and variant the visitor clicked signup from. */
  experiment?: string;
  variant?: string;
}

export const ATTRIBUTION_STORAGE_KEY = "mrg_attribution";

// Saved attribution stops crediting signups this long after the visit that
// brought it, matching the landing pages' window.
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredAttribution {
  attribution: Attribution;
  savedAt: number;
}

// Maps incoming query-string params -> persisted camelCase keys.
const PARAM_TO_KEY: Record<string, keyof Attribution> = {
  ga_client_id: "gaClientId",
  ga_session_id: "gaSessionId",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  utm_placement: "utmPlacement",
  gclid: "gclid",
  fbclid: "fbclid",
  experiment: "experiment",
  variant: "variant",
};

/**
 * A visitor who opted out (cookie bar or Global Privacy Control) must not have
 * their GA ids reach the backend (which would report them to GA). Campaign
 * tags are first-party and kept.
 */
function withoutGaIdsWhenOptedOut(attribution: Attribution): Attribution {
  if (!trackingOptedOut()) return attribution;
  const { gaClientId, gaSessionId, ...rest } = attribution;
  return rest;
}

/**
 * Reads attribution params from the current URL and, if any are present,
 * replaces whatever is stored with them. Returns the stored attribution (or
 * null when nothing is saved or it has expired). Safe to call on every load.
 */
export function captureAttributionFromUrl(
  search: string = typeof window !== "undefined" ? window.location.search : ""
): Attribution | null {
  try {
    const params = new URLSearchParams(search);
    const fromUrl: Attribution = {};
    for (const [param, key] of Object.entries(PARAM_TO_KEY)) {
      const value = params.get(param);
      if (value) fromUrl[key] = value;
    }

    if (Object.keys(fromUrl).length === 0) {
      // No new attribution in the URL — keep any previously captured value.
      return getStoredAttribution();
    }

    const attribution = withoutGaIdsWhenOptedOut(fromUrl);
    const stored: StoredAttribution = { attribution, savedAt: Date.now() };
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
    return attribution;
  } catch {
    return getStoredAttribution();
  }
}

export function getStoredAttribution(): Attribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredAttribution> | null;
    const fresh =
      typeof stored?.savedAt === "number" &&
      Date.now() - stored.savedAt < ATTRIBUTION_TTL_MS;
    if (!fresh || !stored?.attribution) {
      clearStoredAttribution();
      return null;
    }
    return withoutGaIdsWhenOptedOut(stored.attribution);
  } catch {
    return null;
  }
}

/** Forget saved attribution once it has been sent with a new account. */
export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // localStorage can throw (private mode / blocked); nothing to clear then.
  }
}
